import {
    Alert, Box, Button, Chip, CircularProgress, Collapse, Divider,
    IconButton, LinearProgress, Snackbar, Typography
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import moment from 'moment';
import { DataGrid } from '@mui/x-data-grid';
import { useCallback, useEffect, useRef, useState } from 'react';
import ApiServices from '../../services/ApiServices';

const TERMINAL_STAGES = [
    'OTA_SUCCESS', 'OTA_FAILED', 'DOWNLOAD_FAILED', 'STOPPED',
    'REQUEST_REJECTED_ACTIVE_SESSION', 'STOP_REJECTED_NO_ACTIVE_SESSION',
];
const POLL_INTERVAL_MS = 5000;

const STAGE_LABELS = {
    PENDING:                          'Pending',
    REQUEST_ACCEPTED:                 'Request Accepted',
    REQUEST_REJECTED_ACTIVE_SESSION:  'Rejected — Active Session',
    DOWNLOAD_STARTED:                 'Download Started',
    DOWNLOAD_SUCCESS:                 'Download Success',
    DOWNLOAD_FAILED:                  'Download Failed',
    OTA_INITIATED:                    'OTA Initiated',
    TRANSFER_IN_PROGRESS:             'Transfer In Progress',
    TRANSFER_COMPLETE:                'Transfer Complete',
    INSTALLATION_IN_PROGRESS:         'Installation In Progress',
    OTA_SUCCESS:                      'OTA Success',
    OTA_FAILED:                       'OTA Failed',
    STOP_REQUEST_ACCEPTED:            'Stop Accepted',
    STOPPED:                          'Stopped',
    STOP_REJECTED_NO_ACTIVE_SESSION:  'Stop Rejected — No Active Session',
};

const stageColor = (stage) => {
    if (!stage) return 'default';
    if (stage === 'OTA_SUCCESS' || stage === 'COMPLETED') return 'success';
    if (stage === 'OTA_FAILED' || stage === 'FAILED' || stage === 'DOWNLOAD_FAILED') return 'error';
    if (stage === 'STOPPED' || stage === 'STOP_REJECTED_NO_ACTIVE_SESSION') return 'warning';
    if (stage === 'PENDING') return 'default';
    return 'info';
};

const stageLabel = (stage) => STAGE_LABELS[stage] ?? stage ?? '—';

const DEVICE_COLUMNS = [
    { field: 'deviceMac', headerName: 'Device MAC', flex: 1, sortable: false },
    { field: 'binaryType', headerName: 'Binary', flex: 0.6, sortable: false },
    {
        field: 'stage', headerName: 'Stage', flex: 0.8, sortable: false,
        renderCell: (params) => (
            <Chip label={stageLabel(params.row.stage)} color={stageColor(params.row.stage)} size="small" />
        )
    },
    {
        field: 'progressPercent', headerName: 'Progress', flex: 0.8, sortable: false,
        renderCell: (params) => {
            const pct = params.row.progressPercent ?? 0;
            return (
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress variant="determinate" value={pct} sx={{ flex: 1 }} />
                    <Typography variant="caption">{pct}%</Typography>
                </Box>
            );
        }
    },
    {
        field: 'infoMsg', headerName: 'Last Status', flex: 1.5, sortable: false,
        renderCell: (params) => (
            <Typography variant="caption" sx={{ whiteSpace: 'normal', lineHeight: 1.4, wordBreak: 'break-word' }}>
                {params.row.infoMsg ?? '—'}
            </Typography>
        )
    },
    {
        field: 'errorCode', headerName: 'Error Code', flex: 0.6, sortable: false,
        renderCell: (params) => (
            <Typography variant="caption">
                {params.row.errorCode != null ? params.row.errorCode : '—'}
            </Typography>
        )
    },
    {
        field: 'updatedAt', headerName: 'Updated', flex: 1, sortable: false,
        renderCell: (params) => params.value
            ? moment(params.value).format('DD-MM-YYYY HH:mm:ss')
            : '—'
    },
];

function LogFeed({ logs }) {
    if (!logs || logs.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">No messages yet.</Typography>
        );
    }
    return (
        <Box sx={{
            maxHeight: 400,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            bgcolor: 'background.paper',
        }}>
            {logs.map((log) => (
                <Box
                    key={log.id}
                    sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.75 }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap', minWidth: 75, pt: '2px' }}
                    >
                        {moment(log.createdAt).format('HH:mm:ss')}
                    </Typography>
                    <Chip
                        label={log.deviceMac ? log.deviceMac.slice(-8) : 'Gateway'}
                        size="small"
                        color={log.deviceMac ? 'primary' : 'default'}
                        variant={log.deviceMac ? 'outlined' : 'filled'}
                        sx={{ minWidth: 72, fontSize: '0.65rem', height: 18 }}
                    />
                    <Typography variant="caption" sx={{ wordBreak: 'break-word', lineHeight: 1.5 }}>
                        {log.infoMsg}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function GatewayOtaProgress({ gatewayId, coordinatorVersion }) {
    const [sessionData, setSessionData] = useState({ session: null, firmwares: [], deviceStatuses: [], logs: [] });
    const { session, firmwares, deviceStatuses, logs } = sessionData;
    const [loading, setLoading] = useState(true);
    const [stopping, setStopping] = useState(false);
    const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });
    const [logsOpen, setLogsOpen] = useState(false);
    const intervalRef = useRef(null);

    const showSnack = (message, severity = 'error') =>
        setSnack({ open: true, severity, message });

    const fetchActive = useCallback(() => {
        ApiServices.getActiveOtaSession(gatewayId)
            .then(response => {
                if (response.status === 204 || !response.data) {
                    setSessionData({ session: null, firmwares: [], deviceStatuses: [], logs: [] });
                    clearInterval(intervalRef.current);
                } else {
                    const { session: s, firmwares: fw, deviceStatuses: ds, logs: lg } = response.data;
                    setSessionData({
                        session: s,
                        firmwares: fw ?? [],
                        deviceStatuses: (ds ?? []).slice().sort((a, b) => a.id - b.id),
                        logs: lg ?? [],
                    });
                    if (TERMINAL_STAGES.includes(s?.stage)) {
                        clearInterval(intervalRef.current);
                    }
                }
            })
            .catch(error => {
                console.error(error);
                showSnack(error.response?.data ?? error.message);
                clearInterval(intervalRef.current);
            })
            .finally(() => setLoading(false));
    }, [gatewayId]);

    useEffect(() => {
        fetchActive();
        intervalRef.current = setInterval(fetchActive, POLL_INTERVAL_MS);
        return () => clearInterval(intervalRef.current);
    }, [fetchActive]);

    const handleStop = () => {
        if (!session) return;
        setStopping(true);
        ApiServices.stopGatewayOta({
            gateway_id: gatewayId,
            ota_id: session.otaId,
        })
            .then(() => {
                showSnack('Stop request sent.', 'success');
                fetchActive();
            })
            .catch(error => showSnack(error.response?.data ?? error.message))
            .finally(() => setStopping(false));
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress /></Box>;
    }

    if (!session) {
        return (
            <Box sx={{ mt: 2 }}>
                <Typography color="text.secondary">No active OTA session for this gateway.</Typography>
            </Box>
        );
    }

    const isTerminal = TERMINAL_STAGES.includes(session.stage);
    const isXY  = /^\d+\.\d+$/.test(coordinatorVersion ?? '');
    const isXYZ = /^\d+\.\d+\.\d+/.test(coordinatorVersion ?? '');
    const isV   = (coordinatorVersion ?? '').startsWith('v');
    const deviceColumns = (isXY
        ? DEVICE_COLUMNS.filter(c => c.field !== 'stage' && c.field !== 'progressPercent' && c.field !== 'errorCode')
        : (isXYZ || isV)
            ? DEVICE_COLUMNS
            : DEVICE_COLUMNS.filter(c => c.field !== 'errorCode')
    ).filter(c => isTerminal ? c.field !== 'progressPercent' : true);
    const completed = session.completedDevices ?? 0;
    const total = session.totalDevices ?? 0;
    const overallPct = total > 0 ? Math.round((completed / total) * 100) : 0;

    return (
        <Box>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={4000}
                open={snack.open}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
            >
                <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>

            {/* Summary + Firmwares side by side */}
            <Box sx={{ display: 'flex', gap: 3, mb: 1, alignItems: 'flex-start' }}>
                {/* Left: session details */}
                <Box sx={{ flex: 1 }}>
                    <Typography variant="body2"><b>OTA ID:</b> {session.otaId}</Typography>
                    <Typography variant="body2"><b>Stage:</b>{' '}
                        <Chip label={stageLabel(session.stage)} color={stageColor(session.stage)} size="small" />
                    </Typography>
                    <Typography variant="body2">
                        <b>Progress:</b> {completed} / {total} devices
                    </Typography>
                    <Typography variant="body2"><b>Started by:</b> {session.updatedBy ?? '—'}</Typography>
                    {isXY && session.startTime != null && (
                        <Typography variant="body2"><b>Start Time:</b> {session.startTime}s</Typography>
                    )}
                    {isXY && session.stopTime != null && (
                        <Typography variant="body2"><b>Stop Time:</b> {session.stopTime}s</Typography>
                    )}
                    {session.infoMsg && (
                        <Typography variant="body2" color="text.secondary">
                            <b>Gateway:</b> {session.infoMsg}
                        </Typography>
                    )}
                </Box>

                {/* Right: firmware list */}
                {firmwares.length > 0 && (
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Firmwares in session</Typography>
                        {firmwares.map(fw => (
                            <Typography key={fw.id} variant="body2">
                                {fw.binaryType} — {fw.version}
                            </Typography>
                        ))}
                    </Box>
                )}

                {/* Stop button — top right */}
                {!isTerminal && !isXY && (
                    <Button variant="outlined" color="error" onClick={handleStop} disabled={stopping} sx={{ flexShrink: 0 }}>
                        {stopping ? 'Stopping...' : 'Stop OTA'}
                    </Button>
                )}
            </Box>

            <LinearProgress
                variant="determinate"
                value={overallPct}
                sx={{ mb: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary">{overallPct}% complete</Typography>

            {/* Status message log */}
            <Divider sx={{ my: 1.5 }} />
            <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: logsOpen ? 1 : 0 }}
                onClick={() => setLogsOpen(o => !o)}
            >
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    Status Log {logs.length > 0 && `(${logs.length})`}
                </Typography>
                <IconButton size="small">
                    {logsOpen ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </Box>
            <Collapse in={logsOpen}>
                <LogFeed logs={logs} />
            </Collapse>

            {/* Per-device status */}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Per-device status</Typography>
            <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                <DataGrid
                    rows={deviceStatuses}
                    columns={deviceColumns}
                    getRowId={(row) => row.id}
                    autoHeight
                    hideFooter
                    disableColumnMenu
                    disableSelectionOnClick
                    getRowHeight={() => 'auto'}
                    getEstimatedRowHeight={() => 52}
                    sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
                    slots={{
                        noRowsOverlay: () => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography color="text.secondary">No device status yet</Typography>
                            </Box>
                        )
                    }}
                />
            </Box>

            {!isTerminal && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Auto-refreshing every {POLL_INTERVAL_MS / 1000}s…
                </Typography>
            )}
        </Box>
    );
}

export default GatewayOtaProgress;